// src/pages/RecursosAgenticAI.jsx
// Página: /recursos/agentic-ai
// Demo visual pedagógica — arquitectura Agentic AI

import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import AgenticFlowDemo from '../components/AgenticFlowDemo';
import ShareButton from '../components/ShareButton';

const PROCESS_STATE_SUMMARY = {
  structured: {
    header: 'Intervención habilitada',
    headerCls: 'text-emerald-300',
    bgCls: 'bg-emerald-950/30 border-emerald-800/40',
    items: [
      { Icon: CheckCircle, text: 'Reglas claras',       iconCls: 'text-emerald-400' },
      { Icon: CheckCircle, text: 'Datos confiables',    iconCls: 'text-emerald-400' },
      { Icon: CheckCircle, text: 'Acciones definidas',  iconCls: 'text-emerald-400' },
    ],
  },
  unstructured: {
    header: 'Intervención no habilitada',
    headerCls: 'text-amber-300',
    bgCls: 'bg-amber-950/30 border-amber-800/40',
    items: [
      { Icon: AlertTriangle, text: 'Reglas ambiguas',        iconCls: 'text-amber-400' },
      { Icon: AlertTriangle, text: 'Datos inconsistentes',   iconCls: 'text-amber-400' },
      { Icon: AlertTriangle, text: 'Acciones no definidas',  iconCls: 'text-amber-400' },
    ],
  },
};

export default function RecursosAgenticAI() {
  const [mode, setMode] = useState('structured');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'LearningResource',
    name: 'Cómo opera un sistema Agentic AI sobre procesos estructurados',
    description:
      'Demo visual pedagógica: muestra cómo un sistema Agentic AI interpreta señales, consulta contexto, usa herramientas y ejecuta intervenciones cuando el proceso tiene reglas y datos — o escala a revisión humana cuando no.',
    educationalLevel: 'General',
    teaches: 'Agentic AI, arquitectura de agentes, RAG, orquestación IA, procesos estructurados',
    author: { '@type': 'Organization', name: 'Edutech Pulse Lab', url: 'https://edutechpulselab.com' },
  };

  return (
    <>
      <Helmet>
        <title>Cómo opera un sistema Agentic AI sobre procesos estructurados | Edutech Pulse Lab</title>
        <meta
          name="description"
          content="Demo visual pedagógica: observa cómo un agente IA interpreta señales, consulta contexto, usa herramientas y ejecuta intervenciones cuando el proceso tiene estructura — o escala a revisión humana cuando no. Caso: retail / supply chain."
        />
        <meta
          name="keywords"
          content="agentic AI, agentes IA, RAG, memoria contextual, orquestación IA, arquitectura agentes, procesos estructurados, inteligencia artificial, supply chain IA"
        />
        <meta property="og:title" content="Cómo opera un sistema Agentic AI sobre procesos estructurados" />
        <meta
          property="og:description"
          content="Demo visual: la IA no organiza el caos. Opera sobre procesos con reglas, datos y puntos de acción definidos. Explora los modos estructurado y no estructurado."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://edutechpulselab.com/recursos/agentic-ai" />
        <meta property="og:image" content="https://edutechpulselab.com/OG_GRAFICOS/OG_recurso_agenticai.jpeg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://edutechpulselab.com/OG_GRAFICOS/OG_recurso_agenticai.jpeg" />
        <meta name="twitter:title" content="Cómo opera un sistema Agentic AI — Demo Visual" />
        <meta
          name="twitter:description"
          content="Demo pedagógica Agentic AI: modos Estructurado y No estructurado con caso retail. Sin backend, sin inputs libres."
        />
        <link rel="canonical" href="https://edutechpulselab.com/recursos/agentic-ai" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <Navbar />

      <main
        className="min-h-screen"
        style={{ background: 'linear-gradient(180deg, #020617 0%, #0f0c29 50%, #020617 100%)' }}
      >
        {/* Breadcrumb */}
        <div className="border-b border-slate-900/60">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2.5 sm:py-4">
            <Link
              to="/recursos"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-slate-500 hover:text-slate-300 transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              Volver a Recursos
            </Link>
          </div>
        </div>

        {/* Hero */}
        <section className="text-center px-4 sm:px-8 pt-8 sm:pt-16 pb-6 sm:pb-10">
          <div className="max-w-3xl mx-auto">
            <span className="inline-block text-xs font-bold tracking-[0.2em] uppercase text-violet-400 border border-violet-800/60 bg-violet-950/40 px-3 py-1 rounded-full mb-4 sm:mb-6">
              Arquitectura Agentic AI
            </span>
            <h1 className="text-2xl sm:text-3xl sm:text-4xl font-bold text-white leading-tight mb-3 sm:mb-5">
              {/* Mobile: título corto */}
              <span className="sm:hidden">Cómo opera un sistema Agentic AI</span>
              {/* Desktop: título completo */}
              <span className="hidden sm:inline">
                Cómo opera un sistema Agentic AI
                <br /> sobre procesos estructurados
              </span>
            </h1>
            <p className="text-sm sm:text-base sm:text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
              {/* Mobile: subtítulo compacto */}
              <span className="sm:hidden">La IA no organiza el caos. Interviene cuando el proceso tiene reglas, datos y puntos de acción definidos.</span>
              {/* Desktop: subtítulo completo */}
              <span className="hidden sm:inline">La IA no organiza el caos. Interpreta señales, consulta contexto, usa herramientas y
              ejecuta acciones cuando el proceso tiene reglas, datos y puntos de intervención definidos.</span>
            </p>
            <div className="mt-5 flex justify-center">
              <ShareButton
                url="https://edutechpulselab.com/recursos/agentic-ai"
                title="Cómo opera un sistema Agentic AI — Demo Visual"
                text="Demo pedagógica: la IA no organiza el caos. Interviene cuando el proceso tiene reglas, datos y puntos de acción definidos."
              />
            </div>
          </div>
        </section>

        {/* Zona de control: 3 filas jerárquicas */}
        <section className="max-w-6xl mx-auto px-4 sm:px-8 pb-4 sm:pb-6 space-y-2 sm:space-y-3">
          {/* Fila 1: Selector principal del estado del proceso */}
          <div className="space-y-1.5 sm:space-y-2">
            <span className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
              Estado del proceso
            </span>
            <div className="flex rounded-lg border border-slate-700 bg-slate-900/60 p-0.5 gap-0.5 w-fit">
              <button
                onClick={() => setMode('structured')}
                className={`text-sm font-medium px-4 py-2.5 sm:py-2 rounded-md transition-all duration-200 ${
                  mode === 'structured'
                    ? 'bg-emerald-900/70 text-emerald-200 border border-emerald-800/60 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Proceso estructurado
              </button>
              <button
                onClick={() => setMode('unstructured')}
                className={`text-sm font-medium px-4 py-2.5 sm:py-2 rounded-md transition-all duration-200 ${
                  mode === 'unstructured'
                    ? 'bg-amber-900/70 text-amber-200 border border-amber-800/60 shadow-sm'
                    : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                Proceso no estructurado
              </button>
            </div>
          </div>

          {/* Fila 2: Metadatos del caso */}
          <div className="flex flex-wrap gap-2">
            <span className="text-xs text-slate-500 bg-slate-900/40 border border-slate-800/60 px-2.5 py-1 rounded">
              Caso: abastecimiento retail
            </span>
            <span className="text-xs text-slate-500 bg-slate-900/40 border border-slate-800/60 px-2.5 py-1 rounded">
              Entrada: señal operativa
            </span>
          </div>

          {/* Fila 3: Resumen del estado del proceso */}
          <div className={`rounded-lg border px-3 sm:px-4 py-2 sm:py-3 transition-all duration-300 ${PROCESS_STATE_SUMMARY[mode].bgCls}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
              <span className={`text-sm font-semibold ${PROCESS_STATE_SUMMARY[mode].headerCls}`}>
                {PROCESS_STATE_SUMMARY[mode].header}
              </span>
              <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-4 gap-y-1.5 sm:gap-y-2">
                {PROCESS_STATE_SUMMARY[mode].items.map((item) => {
                  const ItemIcon = item.Icon;
                  return (
                    <span key={item.text} className="flex items-center gap-1.5 text-xs text-slate-300">
                      <ItemIcon className={`w-3 h-3 sm:w-3.5 sm:h-3.5 ${item.iconCls}`} />
                      {item.text}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Demo principal */}
        <AgenticFlowDemo mode={mode} />

        {/* Contexto pedagógico */}
        <section className="max-w-6xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
          <div className="grid sm:grid-cols-2 gap-8 border-t border-slate-900/60 pt-16">
            <div>
              <h3 className="text-lg font-bold text-white mb-3">¿Qué es un sistema Agentic AI?</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Un sistema Agentic AI combina un modelo de lenguaje con herramientas externas, memoria de
                contexto y bases de conocimiento. A diferencia de un chatbot, interpreta señales, toma
                decisiones, ejecuta acciones y adapta su comportamiento según el estado del proceso.
              </p>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-3">Por qué importa la estructura del proceso</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                Sin reglas claras, datos confiables y puntos de acción definidos, la IA no puede intervenir
                con precisión. En retail, logística o finanzas, la calidad del proceso determina qué puede
                ejecutar el agente y qué debe escalar a revisión humana.
              </p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-slate-900/60 py-8 sm:py-12">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 text-center">
            <p className="text-slate-500 text-sm mb-4">Explora más herramientas y recursos</p>
            <Link
              to="/recursos"
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-slate-600 text-slate-300 hover:text-white font-medium rounded-lg transition-all text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Ver todos los recursos
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}
